import { Component, Host, h, Element, Prop, Watch, Listen, State } from '@stencil/core';
import debounce from 'debounce';

export interface SdQuickfactElement extends HTMLElement {
  hide: () => void;
  show: () => void;
}

interface UiCmsQuickfactGroupBody {
  iconSize?: 'small' | 'large';
  iconLayout?: 'above' | 'left';
}

export interface GridMapEntry {
  quickfact: SdQuickfactElement;
  position: { row: number; column: number };
}

@Component({
  tag: 'ui-cms-quickfact-group',
  styleUrls: { default: 'ui-cms-quickfact-group.scss', editing: 'ui-cms-quickfact-group.magnolia.scss' },
})
export class UiCmsQuickfactGroup {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsQuickfactGroupBody = {};

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ mutable: true }) quickfacts: SdQuickfactElement[];

  @Prop() inverted: boolean;

  @State() currentQuickfactGroupWidth: number;

  @State() activeRow: number;

  @State() activeQuickfact: SdQuickfactElement;

  private resizeObserver: ResizeObserver;

  private tempResizeObserver: ResizeObserver;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  // Handles the 'sd-show' event, showing the active quickfact and hiding others
  @Listen('sd-show')
  handleQuickfactShow(event: CustomEvent) {
    this.activeQuickfact = event.target as SdQuickfactElement;

    this.quickfacts.forEach((qf) => {
      if (qf !== this.activeQuickfact) {
        qf.hide();
        // add hidden class from quickfact
        qf.classList.add('hidden');
      } else {
        // remove hidden class from quickfact
        qf.classList.remove('hidden');
      }
    });

    this.activeRow = this.getRow(this.activeQuickfact);

    // Add a temp resize observer because images are loaded later and cause layout shifts
    if (this.tempResizeObserver) this.tempResizeObserver.disconnect();
    this.tempResizeObserver = new ResizeObserver(
      debounce(() => {
        const height = this.getQuickfactContentHeight(this.activeQuickfact);

        this.quickfacts.forEach((qf) => {
          const quickfactRow = this.getRow(qf);
          if (quickfactRow === this.activeRow) {
            Object.assign(qf.style, { marginBottom: `${height}px` });
          }
        });
      }, 200)
    );
    this.tempResizeObserver.observe(this.activeQuickfact.querySelector('.slot'));
  }

  // Handles the 'sd-hide' event, resetting the margin for the hidden quickfact
  @Listen('sd-hide')
  handleQuickfactHide(event: CustomEvent) {
    const quickfact = event.target as SdQuickfactElement;
    const quickfactRow = this.getRow(quickfact);

    this.quickfacts.forEach((qf) => {
      const qfRow = this.getRow(qf);
      const isSameRow = qfRow === quickfactRow;
      const shouldResetMargin = quickfact === this.activeQuickfact || (isSameRow && this.getRow(qf) !== this.activeRow);

      if (shouldResetMargin) {
        Object.assign(qf.style, { marginBottom: '0px' });
        // add hidden class from quickfact
        qf.classList.add('hidden');
      }
    });
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  componentDidLoad() {
    this.quickfacts = [].slice.call(this.el.querySelectorAll('sd-quickfact'));
    if (this.inverted) {
      const uiCmsQuickfacts = this.el.querySelectorAll('ui-cms-quickfact');
      uiCmsQuickfacts.forEach((qf) => {
        qf.setAttribute('inverted', 'true');
      });
    }
    this.updateQuickfactIcons();
    this.addResizeObserver();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.tempResizeObserver) this.tempResizeObserver.disconnect();
  }

  private addResizeObserver() {
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.equalizeHeights();
      }, 200)
    );
    this.resizeObserver.observe(this.el);
  }

  // Creates a grid map of quickfacts based on their positions in the DOM
  private createGridMap() {
    const gridMap: { [key: number]: GridMapEntry } = {};
    const { numberOfRows, numberOfColumns, positions } = this.getPositions(this.el, '.quickfact-group');
    this.quickfacts.forEach((quickfact, index) => {
      gridMap[index] = { quickfact, position: positions[index] };
    });

    return { gridMap, numberOfRows, numberOfColumns };
  }

  // Retrieves the row number of a given quickfact
  private getRow(quickfact: HTMLElement): number | undefined {
    const { gridMap } = this.createGridMap();

    const entry = Object.values(gridMap).find((value) => value.quickfact === quickfact);

    if (entry) return entry.position.row;

    return undefined;
  }

  // Equalizes the heights of the summaries within the same row
  private equalizeHeights() {
    if (window.innerWidth < 590) {
      this.resetHeights();
      return;
    }
    const { gridMap, numberOfRows } = this.createGridMap();

    for (let x = 1; x <= numberOfRows; x += 1) {
      const summariesOnRow = [];
      for (let i = 0, n = Object.values(gridMap).length; i < n; i += 1) {
        const value = Object.values(gridMap)[i];
        if (value.position.row === x) {
          summariesOnRow.push(value.quickfact.shadowRoot.querySelector('[part~="summary"]'));
        }
      }

      summariesOnRow.forEach((summary) => {
        summary.style.setProperty('height', 'auto');
      });

      const maxHeight = Math.max(...Array.from(summariesOnRow).map((summary) => summary.clientHeight));

      summariesOnRow.forEach((summary) => {
        summary.style.setProperty('height', `${maxHeight}px`);
      });
    }
  }

  // Resets the height of all summaries to auto for mobile view
  private resetHeights() {
    this.quickfacts.forEach((quickfact) => {
      const summary = quickfact.shadowRoot.querySelector('[part~="summary"]') as HTMLElement;
      if (summary) {
        summary.style.setProperty('height', 'auto');
      }
    });
  }

  private updateQuickfactIcons() {
    const quickfacts = [].slice.call(this.el.querySelectorAll('ui-cms-quickfact'));
    quickfacts.forEach((quickfact: any) => {
      const updatedQuickfact = quickfact;
      updatedQuickfact.iconSize = this.body.iconSize;
      updatedQuickfact.iconLayout = this.body.iconLayout;
    });
  }

  private getQuickfactContentHeight(quickfact) {
    const content = quickfact.shadowRoot.querySelector('[part~="content__slot"]');

    return content.offsetHeight;
  }

  private getPositions(element: HTMLElement, querySelectorName: string) {
    const grid = element.querySelector(querySelectorName);
    const gridComputedStyle = window.getComputedStyle(grid);

    const numberOfRows = gridComputedStyle.getPropertyValue('grid-template-rows').split(' ').length;

    const numberOfColumns = gridComputedStyle.getPropertyValue('grid-template-columns').split(' ').length;
    const positions = [];

    for (let row = 1; row <= numberOfRows; row += 1) {
      for (let col = 1; col <= numberOfColumns; col += 1) {
        positions.push({ row, column: col });
      }
    }
    return { numberOfRows, numberOfColumns, positions };
  }

  render() {
    return (
      <Host>
        <div class="quickfact-group">
          <slot></slot>
        </div>
      </Host>
    );
  }
}
